import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  FileText,
  MessageSquare,
  LogOut,
  Plus,
  Edit,
  Trash2,
  Check,
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const AdminDashboard = () => {
  const { t } = useTranslation();
  const { token, logout, admin } = useAuth();
  const [activeTab, setActiveTab] = useState('blog');
  const [posts, setPosts] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isPostModalOpen, setIsPostModalOpen] = useState(false);
  const [editingPost, setEditingPost] = useState(null);
  const [postForm, setPostForm] = useState({
    title_tr: '',
    title_en: '',
    content_tr: '',
    content_en: '',
    excerpt_tr: '',
    excerpt_en: '',
    category: '',
    image_url: '',
    is_published: true,
  });

  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    fetchData();
  }, [activeTab]);

  const fetchData = async () => {
    setLoading(true);
    try {
      if (activeTab === 'blog') {
        const response = await axios.get(`${API}/blog?published_only=false`, { headers });
        setPosts(response.data);
      } else if (activeTab === 'contacts') {
        const response = await axios.get(`${API}/contact`, { headers });
        setContacts(response.data);
      }
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handlePostFormChange = (e) => {
    const { name, value } = e.target;
    setPostForm({ ...postForm, [name]: value });
  };

  const handlePostSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingPost) {
        await axios.put(`${API}/blog/${editingPost.id}`, postForm, { headers });
      } else {
        await axios.post(`${API}/blog`, postForm, { headers });
      }
      setIsPostModalOpen(false);
      setEditingPost(null);
      resetPostForm();
      fetchData();
    } catch (error) {
      console.error('Error saving post:', error);
    }
  };

  const handleEditPost = (post) => {
    setEditingPost(post);
    setPostForm({
      title_tr: post.title_tr,
      title_en: post.title_en,
      content_tr: post.content_tr,
      content_en: post.content_en,
      excerpt_tr: post.excerpt_tr,
      excerpt_en: post.excerpt_en,
      category: post.category,
      image_url: post.image_url,
      is_published: post.is_published,
    });
    setIsPostModalOpen(true);
  };

  const handleDeletePost = async (postId) => {
    if (window.confirm('Bu yazıyı silmek istediğinize emin misiniz?')) {
      try {
        await axios.delete(`${API}/blog/${postId}`, { headers });
        fetchData();
      } catch (error) {
        console.error('Error deleting post:', error);
      }
    }
  };

  const handleMarkAsRead = async (contactId) => {
    try {
      await axios.put(`${API}/contact/${contactId}/read`, {}, { headers });
      fetchData();
    } catch (error) {
      console.error('Error marking contact as read:', error);
    }
  };

  const handleDeleteContact = async (contactId) => {
    if (window.confirm('Bu mesajı silmek istediğinize emin misiniz?')) {
      try {
        await axios.delete(`${API}/contact/${contactId}`, { headers });
        fetchData();
      } catch (error) {
        console.error('Error deleting contact:', error);
      }
    }
  };

  const resetPostForm = () => {
    setPostForm({
      title_tr: '',
      title_en: '',
      content_tr: '',
      content_en: '',
      excerpt_tr: '',
      excerpt_en: '',
      category: '',
      image_url: '',
      is_published: true,
    });
  };

  const openNewPostModal = () => {
    setEditingPost(null);
    resetPostForm();
    setIsPostModalOpen(true);
  };

  const sidebarItems = [
    { id: 'blog', icon: FileText, label: t('admin.blogManagement') },
    { id: 'contacts', icon: MessageSquare, label: t('admin.contacts') },
  ];

  return (
    <div data-testid="admin-dashboard" className="min-h-screen bg-[#0F1A2E] flex">
      {/* Sidebar */}
      <aside className="w-64 bg-[#0A1423] border-r border-[#C9A84C]/10 flex flex-col">
        <div className="p-6 border-b border-[#C9A84C]/10">
          <div className="flex items-center gap-3 mb-2">
            {/* Logo Icon */}
            <div className="text-[#C9A84C]">
              <svg width="40" height="40" viewBox="0 0 100 100" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M50 5L90 25V75L50 95L10 75V25L50 5Z" stroke="currentColor" strokeWidth="2" fill="none"/>
                <path d="M50 15L80 30V70L50 85L20 70V30L50 15Z" stroke="currentColor" strokeWidth="1.5" fill="none"/>
                <path d="M35 45L50 35L65 45" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                <path d="M35 55L50 45L65 55" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                <path d="M35 65L50 55L65 65" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
              </svg>
            </div>
            {/* Logo Text */}
            <div className="flex flex-col">
              <span className="text-[#C9A84C] font-heading text-xl font-bold tracking-wide">BEU</span>
              <span className="text-[#C9A84C]/80 text-[8px] tracking-widest uppercase">Finansal Danışmanlık</span>
            </div>
          </div>
          <p className="text-white/50 text-sm">{admin?.name}</p>
        </div>

        <nav className="flex-1 p-4">
          {sidebarItems.map((item) => (
            <button
              key={item.id}
              onClick={() => setActiveTab(item.id)}
              data-testid={`sidebar-${item.id}`}
              className={`w-full flex items-center gap-3 px-4 py-3 mb-2 transition-all duration-300 ${
                activeTab === item.id
                  ? 'bg-[#C9A84C]/10 text-[#C9A84C] border-l-2 border-[#C9A84C]'
                  : 'text-white/60 hover:bg-[#1B2B4B]/50 hover:text-white'
              }`}
            >
              <item.icon size={20} />
              {item.label}
            </button>
          ))}
        </nav>

        <div className="p-4 border-t border-[#C9A84C]/10">
          <button
            onClick={logout}
            data-testid="logout-btn"
            className="w-full flex items-center gap-3 px-4 py-3 text-white/60 hover:bg-[#1B2B4B]/50 hover:text-white transition-all duration-300"
          >
            <LogOut size={20} />
            {t('admin.logout')}
          </button>
        </div>
      </aside>

      {/* Main Content */}
      <main className="flex-1 p-8">
        {/* Blog Management */}
        {activeTab === 'blog' && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            data-testid="blog-management"
          >
            <div className="flex justify-between items-center mb-8">
              <h2 className="font-heading text-2xl font-bold text-white">
                {t('admin.blogManagement')}
              </h2>
              <Button
                onClick={openNewPostModal}
                data-testid="new-post-btn"
                className="bg-[#C9A84C] text-[#0F1A2E] hover:bg-[#E5D49B] rounded-none"
              >
                <Plus size={20} className="mr-2" />
                {t('admin.newPost')}
              </Button>
            </div>

            {loading ? (
              <div className="space-y-4">
                {[1, 2, 3].map((i) => (
                  <div key={i} className="bg-[#1B2B4B]/50 border border-[#C9A84C]/10 p-6 animate-pulse">
                    <div className="h-6 bg-[#1B2B4B] rounded w-1/3 mb-4" />
                    <div className="h-4 bg-[#1B2B4B] rounded w-2/3" />
                  </div>
                ))}
              </div>
            ) : posts.length === 0 ? (
              <div className="text-center py-20 text-white/50">
                Henüz blog yazısı bulunmamaktadır.
              </div>
            ) : (
              <div className="space-y-4">
                {posts.map((post) => (
                  <div
                    key={post.id}
                    data-testid={`post-item-${post.id}`}
                    className="bg-[#1B2B4B]/50 border border-[#C9A84C]/10 p-6 flex items-center justify-between"
                  >
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <h3 className="font-heading text-lg font-semibold text-white">
                          {post.title_tr}
                        </h3>
                        <span
                          className={`px-2 py-1 text-xs ${
                            post.is_published
                              ? 'bg-green-500/20 text-green-400'
                              : 'bg-yellow-500/20 text-yellow-400'
                          }`}
                        >
                          {post.is_published ? t('admin.published') : t('admin.draft')}
                        </span>
                      </div>
                      <p className="text-white/50 text-sm">{post.category}</p>
                    </div>
                    <div className="flex items-center gap-2">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleEditPost(post)}
                        data-testid={`edit-post-${post.id}`}
                        className="text-white/60 hover:text-[#C9A84C]"
                      >
                        <Edit size={18} />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleDeletePost(post.id)}
                        data-testid={`delete-post-${post.id}`}
                        className="text-white/60 hover:text-red-500"
                      >
                        <Trash2 size={18} />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </motion.div>
        )}

        {/* Contacts Management */}
        {activeTab === 'contacts' && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            data-testid="contacts-management"
          >
            <h2 className="font-heading text-2xl font-bold text-white mb-8">
              {t('admin.contacts')}
            </h2>

            {loading ? (
              <div className="space-y-4">
                {[1, 2, 3].map((i) => (
                  <div key={i} className="bg-[#1B2B4B]/50 border border-[#C9A84C]/10 p-6 animate-pulse">
                    <div className="h-6 bg-[#1B2B4B] rounded w-1/3 mb-4" />
                    <div className="h-4 bg-[#1B2B4B] rounded w-2/3" />
                  </div>
                ))}
              </div>
            ) : contacts.length === 0 ? (
              <div className="text-center py-20 text-white/50">
                Henüz iletişim formu bulunmamaktadır.
              </div>
            ) : (
              <div className="space-y-4">
                {contacts.map((contact) => (
                  <div
                    key={contact.id}
                    data-testid={`contact-item-${contact.id}`}
                    className={`bg-[#1B2B4B]/50 border p-6 ${
                      contact.is_read ? 'border-[#C9A84C]/10' : 'border-[#C9A84C]/40'
                    }`}
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-2">
                          <h3 className="font-semibold text-white">{contact.name}</h3>
                          {!contact.is_read && (
                            <span className="px-2 py-1 text-xs bg-[#C9A84C]/20 text-[#C9A84C]">
                              Yeni
                            </span>
                          )}
                        </div>
                        <p className="text-white/50 text-sm mb-1">{contact.email}</p>
                        {contact.phone && (
                          <p className="text-white/50 text-sm mb-1">{contact.phone}</p>
                        )}
                        {contact.company && (
                          <p className="text-white/50 text-sm mb-3">{contact.company}</p>
                        )}
                        <p className="text-white/70 mt-4 border-t border-[#C9A84C]/10 pt-4">
                          {contact.message}
                        </p>
                        <p className="text-white/30 text-xs mt-4">
                          {new Date(contact.created_at).toLocaleString('tr-TR')}
                        </p>
                      </div>
                      <div className="flex items-center gap-2 ml-4">
                        {!contact.is_read && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleMarkAsRead(contact.id)}
                            data-testid={`mark-read-${contact.id}`}
                            className="text-white/60 hover:text-green-500"
                          >
                            <Check size={18} />
                          </Button>
                        )}
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDeleteContact(contact.id)}
                          data-testid={`delete-contact-${contact.id}`}
                          className="text-white/60 hover:text-red-500"
                        >
                          <Trash2 size={18} />
                        </Button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </motion.div>
        )}
      </main>

      {/* Post Modal */}
      <Dialog open={isPostModalOpen} onOpenChange={setIsPostModalOpen}>
        <DialogContent className="bg-[#1B2B4B] border-[#C9A84C]/20 text-white max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading text-2xl">
              {editingPost ? t('admin.editPost') : t('admin.newPost')}
            </DialogTitle>
          </DialogHeader>

          <form onSubmit={handlePostSubmit} data-testid="post-form" className="space-y-6 mt-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-white/70">Başlık (TR) *</Label>
                <Input
                  name="title_tr"
                  required
                  value={postForm.title_tr}
                  onChange={handlePostFormChange}
                  data-testid="post-title-tr"
                  className="bg-[#0F1A2E] border-[#C9A84C]/20 focus:border-[#C9A84C] text-white rounded-none"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-white/70">Title (EN) *</Label>
                <Input
                  name="title_en"
                  required
                  value={postForm.title_en}
                  onChange={handlePostFormChange}
                  data-testid="post-title-en"
                  className="bg-[#0F1A2E] border-[#C9A84C]/20 focus:border-[#C9A84C] text-white rounded-none"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-white/70">Özet (TR) *</Label>
                <Textarea
                  name="excerpt_tr"
                  required
                  rows={3}
                  value={postForm.excerpt_tr}
                  onChange={handlePostFormChange}
                  data-testid="post-excerpt-tr"
                  className="bg-[#0F1A2E] border-[#C9A84C]/20 focus:border-[#C9A84C] text-white rounded-none"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-white/70">Excerpt (EN) *</Label>
                <Textarea
                  name="excerpt_en"
                  required
                  rows={3}
                  value={postForm.excerpt_en}
                  onChange={handlePostFormChange}
                  data-testid="post-excerpt-en"
                  className="bg-[#0F1A2E] border-[#C9A84C]/20 focus:border-[#C9A84C] text-white rounded-none"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-white/70">İçerik (TR) *</Label>
                <Textarea
                  name="content_tr"
                  required
                  rows={8}
                  value={postForm.content_tr}
                  onChange={handlePostFormChange}
                  data-testid="post-content-tr"
                  className="bg-[#0F1A2E] border-[#C9A84C]/20 focus:border-[#C9A84C] text-white rounded-none"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-white/70">Content (EN) *</Label>
                <Textarea
                  name="content_en"
                  required
                  rows={8}
                  value={postForm.content_en}
                  onChange={handlePostFormChange}
                  data-testid="post-content-en"
                  className="bg-[#0F1A2E] border-[#C9A84C]/20 focus:border-[#C9A84C] text-white rounded-none"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-white/70">Kategori *</Label>
                <Input
                  name="category"
                  required
                  value={postForm.category}
                  onChange={handlePostFormChange}
                  data-testid="post-category"
                  placeholder="Finans, Yatırım, Enerji..."
                  className="bg-[#0F1A2E] border-[#C9A84C]/20 focus:border-[#C9A84C] text-white rounded-none"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-white/70">Görsel URL</Label>
                <Input
                  name="image_url"
                  value={postForm.image_url}
                  onChange={handlePostFormChange}
                  data-testid="post-image-url"
                  placeholder="https://..."
                  className="bg-[#0F1A2E] border-[#C9A84C]/20 focus:border-[#C9A84C] text-white rounded-none"
                />
              </div>
            </div>

            <div className="flex items-center gap-3">
              <Switch
                checked={postForm.is_published}
                onCheckedChange={(checked) =>
                  setPostForm({ ...postForm, is_published: checked })
                }
                data-testid="post-published-switch"
              />
              <Label className="text-white/70">
                {postForm.is_published ? t('admin.published') : t('admin.draft')}
              </Label>
            </div>

            <div className="flex justify-end gap-4 pt-4 border-t border-[#C9A84C]/10">
              <Button
                type="button"
                variant="ghost"
                onClick={() => setIsPostModalOpen(false)}
                className="text-white/60"
              >
                {t('admin.cancel')}
              </Button>
              <Button
                type="submit"
                data-testid="save-post-btn"
                className="bg-[#C9A84C] text-[#0F1A2E] hover:bg-[#E5D49B] rounded-none"
              >
                {t('admin.save')}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};
